import React, { useState, useEffect, useRef, useCallback } from 'react';
import ActiveTraderPanel from './ActiveTrader';

const COLORS=['#2563eb','#7c3aed','#059669','#dc2626','#d97706','#0891b2'];
const SC={};
function symCol(s){if(!SC[s])SC[s]=COLORS[Object.keys(SC).length%COLORS.length];return SC[s];}
const fN=(n,d=2)=>n!=null&&!isNaN(n)?Number(n).toFixed(d):'--';

const TETHER_COLORS=[
  {id:'red',   color:'#ef4444',label:'Red'},
  {id:'blue',  color:'#2563eb',label:'Blue'},
  {id:'green', color:'#22c55e',label:'Green'},
  {id:'yellow',color:'#f59e0b',label:'Yellow'},
  {id:'purple',color:'#7c3aed',label:'Purple'},
  {id:'orange',color:'#f97316',label:'Orange'},
];

const PROXY='https://corsproxy.io/?';
const PRE_POST='&includePrePost=true';

function getMarketSession(){
  const et=new Date(new Date().toLocaleString('en-US',{timeZone:'America/New_York'}));
  const m=et.getHours()*60+et.getMinutes(),d=et.getDay();
  if(d===0||d===6)return'closed';
  if(m>=240&&m<570)return'premarket';
  if(m>=570&&m<960)return'market';
  if(m>=960&&m<1200)return'afterhours';
  return'closed';
}

function SessionBadge(){
  const s=getMarketSession();
  if(s==='premarket')  return<span style={{fontSize:9,color:'#f59e0b',padding:'1px 6px',background:'#1c1407',borderRadius:4,border:'1px solid #854d0e',marginLeft:4}}>◑ PRE-MKT</span>;
  if(s==='afterhours') return<span style={{fontSize:9,color:'#a78bfa',padding:'1px 6px',background:'#1a0f2e',borderRadius:4,border:'1px solid #5b21b6',marginLeft:4}}>◑ AFTER HRS</span>;
  if(s==='market')     return<span style={{fontSize:9,color:'#22c55e',padding:'1px 6px',background:'#0d2818',borderRadius:4,border:'1px solid #166534',marginLeft:4}}>● LIVE</span>;
  return<span style={{fontSize:9,color:'#484f58',padding:'1px 6px',background:'#161b22',borderRadius:4,border:'1px solid #30363d',marginLeft:4}}>◌ CLOSED</span>;
}

const TF_MAP={'1m':{interval:'1m',range:'1d'},'5m':{interval:'5m',range:'5d'},'15m':{interval:'15m',range:'5d'},'30m':{interval:'30m',range:'1mo'},'1H':{interval:'60m',range:'1mo'},'4H':{interval:'60m',range:'3mo'},'D':{interval:'1d',range:'1y'}};

async function fetchYahooCandles(sym,tf){
  try{
    const cfg=TF_MAP[tf]||TF_MAP['5m'];
    const url=`https://query1.finance.yahoo.com/v8/finance/chart/${sym}?interval=${cfg.interval}&range=${cfg.range}${PRE_POST}`;
    const r=await fetch(PROXY+encodeURIComponent(url));
    const d=await r.json();
    const result=d?.chart?.result?.[0];
    if(!result)return null;
    const ts=result.timestamp,q=result.indicators.quote[0];
    if(!ts||!q.close)return null;
    const vi=ts.map((_,i)=>i).filter(i=>q.close[i]!=null);
    return{
      t:vi.map(i=>{const dt=new Date(ts[i]*1000);return tf==='D'?dt.toLocaleDateString('en-US',{month:'short',day:'numeric'}):dt.toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'});}),
      o:vi.map(i=>+(q.open[i]||q.close[i]).toFixed(3)),
      h:vi.map(i=>+(q.high[i]||q.close[i]).toFixed(3)),
      l:vi.map(i=>+(q.low[i]||q.close[i]).toFixed(3)),
      c:vi.map(i=>+(q.close[i]).toFixed(3)),
      v:vi.map(i=>q.volume[i]||0),
    };
  }catch{return null;}
}

async function fetchYahooQuote(sym){
  try{
    const url=`https://query1.finance.yahoo.com/v8/finance/chart/${sym}?interval=1m&range=1d${PRE_POST}`;
    const r=await fetch(PROXY+encodeURIComponent(url));
    const d=await r.json();
    const meta=d?.chart?.result?.[0]?.meta;
    if(!meta)return null;
    return{c:meta.regularMarketPrice,pc:meta.previousClose||meta.chartPreviousClose,h:meta.regularMarketDayHigh,l:meta.regularMarketDayLow,o:meta.regularMarketOpen,v:meta.regularMarketVolume};
  }catch{return null;}
}

function calcEMA(d,p){const k=2/(p+1);let e=new Array(d.length).fill(null);let s=0,cnt=0;for(let i=0;i<d.length;i++){if(cnt<p){s+=d[i];cnt++;if(cnt===p)e[i]=s/p;}else e[i]=d[i]*k+e[i-1]*(1-k);}return e;}
function calcVWAP(h,l,c,v){let ct=0,cv=0,r=[];for(let i=0;i<c.length;i++){const tp=(h[i]+l[i]+c[i])/3;ct+=tp*v[i];cv+=v[i];r.push(cv>0?ct/cv:c[i]);}return r;}
function calcBB(c,p=20,m=2){let up=new Array(c.length).fill(null),lo=new Array(c.length).fill(null);for(let i=p-1;i<c.length;i++){const sl=c.slice(i-p+1,i+1),mn=sl.reduce((a,b)=>a+b,0)/p,sd=Math.sqrt(sl.reduce((a,b)=>a+(b-mn)**2,0)/p);up[i]=mn+m*sd;lo[i]=mn-m*sd;}return{up,lo};}

const SCANNER_DATA={
  sc_topgainers:{title:'Top Gainers',desc:'Biggest % movers today',rows:[
    {sym:'BBBY',price:0.72,chg:44.1,vol:'892M',float:'28M',gap:'+38%',rs:97,fsz:'low'},
    {sym:'MULN',price:0.18,chg:32.1,vol:'1.2B',float:'14M',gap:'+28%',rs:94,fsz:'low'},
    {sym:'CLOV',price:1.44,chg:22.8,vol:'287M',float:'198M',gap:'+18%',rs:86,fsz:'mid'},
    {sym:'GME',price:14.72,chg:18.4,vol:'184M',float:'231M',gap:'+12%',rs:91,fsz:'mid'},
    {sym:'AMC',price:3.88,chg:11.2,vol:'312M',float:'289M',gap:'+8%',rs:88,fsz:'mid'},
    {sym:'MARA',price:22.14,chg:6.7,vol:'87M',float:'168M',gap:'+4%',rs:74,fsz:'mid'},
    {sym:'RIOT',price:11.82,chg:5.2,vol:'62M',float:'244M',gap:'+3%',rs:71,fsz:'mid'},
    {sym:'NVDA',price:487.22,chg:3.81,vol:'41.7M',float:'2.46B',gap:'+2%',rs:68,fsz:'hi'},
  ]},
  sc_momentum:{title:'Small Cap HOD Momentum',desc:'Breaking high of day with volume',rows:[
    {sym:'TRAW',price:12.75,chg:18.4,vol:'338K',float:'763K',gap:'+12%',rs:91,fsz:'low'},
    {sym:'MCVT',price:3.65,chg:8.2,vol:'916K',float:'2.39M',gap:'+6%',rs:82,fsz:'mid'},
    {sym:'DATS',price:4.81,chg:6.1,vol:'156K',float:'3.20M',gap:'+4%',rs:74,fsz:'mid'},
    {sym:'SENS',price:0.76,chg:3.2,vol:'106K',float:'591M',gap:'+2%',rs:61,fsz:'low'},
    {sym:'HCWC',price:1.86,chg:2.1,vol:'1.89M',float:'1.89M',gap:'+1%',rs:55,fsz:'mid'},
  ]},
  sc_runningup:{title:'Running Up',desc:'Consecutive green candles',rows:[
    {sym:'ATNE',price:1.98,chg:3.2,vol:'520K',float:'838K',gap:'+0.36%',rs:82,fsz:'low'},
    {sym:'TIRX',price:2.76,chg:4.3,vol:'1.82M',float:'688K',gap:'+4.34%',rs:74,fsz:'low'},
    {sym:'VRME',price:2.57,chg:6.1,vol:'57.44M',float:'7.97M',gap:'+4.15%',rs:68,fsz:'mid'},
  ]},
  sc_afthours:{title:'After Hours Top Gainers',desc:'Moving after market close 4-8pm',rows:[
    {sym:'TRAW',price:12.28,chg:344.42,vol:'342K',float:'8.06M',gap:'+344%',rs:97,fsz:'low'},
    {sym:'SAGE',price:8.11,chg:234.12,vol:'2.17M',float:'5.55M',gap:'+234%',rs:94,fsz:'mid'},
    {sym:'TIRX',price:1.93,chg:84.76,vol:'3.04M',float:'1.37M',gap:'+84%',rs:88,fsz:'low'},
    {sym:'RHE',price:2.45,chg:22.4,vol:'1.08M',float:'2.23M',gap:'+22%',rs:81,fsz:'low'},
  ]},
  sc_continuation:{title:'Continuation',desc:'Previous day runners setting up again',rows:[
    {sym:'ARBEW',price:0.83,chg:12.4,vol:'286K',float:'27.35M',gap:'+8%',rs:89,fsz:'low'},
    {sym:'CGBS',price:0.96,chg:8.2,vol:'99.07M',float:'5717M',gap:'+5%',rs:82,fsz:'hi'},
    {sym:'NRXP',price:4.85,chg:6.1,vol:'2.49M',float:'8.94M',gap:'+4%',rs:76,fsz:'mid'},
    {sym:'SPCB',price:9.86,chg:4.3,vol:'578K',float:'852K',gap:'+3%',rs:71,fsz:'low'},
    {sym:'FAAS',price:1.10,chg:2.1,vol:'723K',float:'2.68K',gap:'+1%',rs:61,fsz:'low'},
  ]},
  lc_momentum:{title:'Large Cap Momentum',desc:'Large cap biggest movers today',rows:[
    {sym:'NVDA',price:487.22,chg:3.81,vol:'41.7M',float:'2.46B',gap:'+2%',rs:68,fsz:'hi'},
    {sym:'META',price:312.44,chg:1.87,vol:'22.1M',float:'2.14B',gap:'+1%',rs:55,fsz:'hi'},
    {sym:'TSLA',price:174.91,chg:2.14,vol:'92.1M',float:'3.17B',gap:'+1%',rs:61,fsz:'hi'},
    {sym:'AAPL',price:187.42,chg:1.23,vol:'48.2M',float:'15.4B',gap:'0%',rs:49,fsz:'hi'},
    {sym:'AMD',price:164.33,chg:0.92,vol:'38.4M',float:'1.61B',gap:'0%',rs:52,fsz:'hi'},
  ]},
};

// ── TETHER BUTTON ──
function TetherBtn({tetherId,onChangeTether}){
  const [open,setOpen]=useState(false);
  const tc=TETHER_COLORS.find(c=>c.id===tetherId);
  return(
    <div style={{position:'relative',flexShrink:0}}>
      <button
        className="tether-btn"
        style={{background:tc?tc.color+'33':'none',borderColor:tc?tc.color:'#30363d',color:tc?tc.color:'#6b7280'}}
        onClick={e=>{e.stopPropagation();setOpen(o=>!o);}}
        title="Link group — panels with same color sync when you click a symbol"
      >
        <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
          <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
        </svg>
        {tc&&<div style={{width:6,height:6,borderRadius:'50%',background:tc.color,marginLeft:2}}/>}
      </button>
      {open&&(
        <div className="tether-menu" onClick={e=>e.stopPropagation()}>
          <div style={{fontSize:11,fontWeight:700,color:'#e6edf3',marginBottom:3}}>Link Group</div>
          <div style={{fontSize:9,color:'#484f58',lineHeight:1.4,marginBottom:10}}>Panels in the same color group all update when you select a symbol from a linked scanner.</div>
          <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
            <div
              style={{width:28,height:28,borderRadius:7,background:'#21262d',border:`2px solid ${!tetherId?'#fff':'#30363d'}`,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}
              onClick={()=>{onChangeTether(null);setOpen(false);}}
              title="No group"
            >
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="#6b7280" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </div>
            {TETHER_COLORS.map(t=>(
              <div
                key={t.id}
                style={{width:28,height:28,borderRadius:7,background:t.color+'33',border:`2px solid ${tetherId===t.id?t.color:'transparent'}`,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',transition:'transform .1s'}}
                onClick={()=>{onChangeTether(t.id);setOpen(false);}}
                title={t.label}
              >
                <div style={{width:14,height:14,borderRadius:'50%',background:t.color}}/>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── POPOUT BUTTON ──
function PopoutBtn({onPopout}){
  return(
    <button
      className="cp-icon-btn"
      onClick={onPopout}
      title="Pop out to new window"
      style={{marginLeft:2,color:'#6b7280'}}
    >
      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
        <polyline points="15 3 21 3 21 9"/>
        <line x1="10" y1="14" x2="21" y2="3"/>
      </svg>
    </button>
  );
}

// ── CHART CANVAS ──
function ChartCanvas({panel}){
  const ref=useRef(null);
  useEffect(()=>{
    const canvas=ref.current;
    if(!canvas)return;
    const wrap=canvas.parentElement;
    canvas.width=wrap.clientWidth||400;canvas.height=wrap.clientHeight||260;
    const data=panel.data;
    if(!data||!data.c||data.c.length===0)return;
    const ctx=canvas.getContext('2d');
    const W=canvas.width,H=canvas.height;
    ctx.fillStyle=panel.bgCol||'#0d1117';ctx.fillRect(0,0,W,H);
    const pad={top:8,right:58,bottom:20,left:6};
    const cW=W-pad.left-pad.right,cH=H-pad.top-pad.bottom;
    const n=data.c.length;
    if(n===0||cW<=0||cH<=0)return;
    let minP=Math.min(...data.l),maxP=Math.max(...data.h);
    const rng=maxP-minP||1;minP-=rng*0.04;maxP+=rng*0.04;
    const totalR=maxP-minP;
    const yS=vl=>pad.top+cH*(1-(vl-minP)/totalR);
    const bW=Math.max(1,(cW/n)*0.65);
    const xOf=i=>pad.left+((i+0.5)/n)*cW;
    ctx.font='8px monospace';
    for(let i=0;i<=5;i++){const val=minP+(totalR/5)*i,y=yS(val);ctx.strokeStyle='#21262d';ctx.lineWidth=0.5;ctx.beginPath();ctx.moveTo(pad.left,y);ctx.lineTo(pad.left+cW,y);ctx.stroke();ctx.fillStyle='#484f58';ctx.textAlign='left';ctx.fillText('$'+fN(val),W-pad.right+3,y+3);}
    const xStep=Math.max(1,Math.floor(n/6));
    for(let i=0;i<n;i+=xStep){ctx.fillStyle='#484f58';ctx.textAlign='center';ctx.fillText(data.t[i],xOf(i),H-3);}
    const ct=panel.chartType||'candlestick';
    if(ct==='line'||ct==='area'){
      if(ct==='area'){ctx.beginPath();data.c.forEach((vl,i)=>{const x=xOf(i);if(i===0)ctx.moveTo(x,yS(vl));else ctx.lineTo(x,yS(vl));});ctx.lineTo(xOf(n-1),pad.top+cH);ctx.lineTo(xOf(0),pad.top+cH);ctx.closePath();ctx.fillStyle=(panel.upCol||'#22c55e')+'22';ctx.fill();}
      ctx.beginPath();ctx.strokeStyle=panel.upCol||'#22c55e';ctx.lineWidth=1.5;data.c.forEach((vl,i)=>{const x=xOf(i);if(i===0)ctx.moveTo(x,yS(vl));else ctx.lineTo(x,yS(vl));});ctx.stroke();
    }else{
      for(let i=0;i<n;i++){const x=xOf(i),isUp=data.c[i]>=data.o[i],col=isUp?(panel.upCol||'#22c55e'):(panel.dnCol||'#ef4444');ctx.strokeStyle=col;ctx.lineWidth=0.8;ctx.beginPath();ctx.moveTo(x,yS(data.h[i]));ctx.lineTo(x,yS(data.l[i]));ctx.stroke();if(ct==='candlestick'||ct==='heikinashi'){const by=yS(Math.max(data.o[i],data.c[i])),bh=Math.max(1,Math.abs(yS(data.o[i])-yS(data.c[i])));ctx.fillStyle=col;ctx.fillRect(x-bW/2,by,bW,bh);}else if(ct==='ohlc'){ctx.lineWidth=1;ctx.beginPath();ctx.moveTo(x-bW/2,yS(data.o[i]));ctx.lineTo(x,yS(data.o[i]));ctx.stroke();ctx.beginPath();ctx.moveTo(x,yS(data.c[i]));ctx.lineTo(x+bW/2,yS(data.c[i]));ctx.stroke();}}
    }
    function drawLine(vals,col,dashed){const pts=vals.map((vl,i)=>vl!=null?{x:xOf(i),y:yS(vl)}:null);let started=false;ctx.strokeStyle=col;ctx.lineWidth=1;if(dashed)ctx.setLineDash([4,3]);else ctx.setLineDash([]);ctx.beginPath();pts.forEach(pt=>{if(!pt)return;if(!started){ctx.moveTo(pt.x,pt.y);started=true;}else ctx.lineTo(pt.x,pt.y);});ctx.stroke();ctx.setLineDash([]);}
    const inds=panel.inds||{vwap:true,ema9:true,ema20:true};
    if(inds.vwap)drawLine(calcVWAP(data.h,data.l,data.c,data.v),'#a78bfa',true);
    if(inds.ema9)drawLine(calcEMA(data.c,9),'#fbbf24',false);
    if(inds.ema20)drawLine(calcEMA(data.c,20),'#34d399',false);
    if(inds.ema200)drawLine(calcEMA(data.c,200),'#f472b6',true);
    if(inds.bb){const bb=calcBB(data.c);drawLine(bb.up,'#60a5fa',true);drawLine(bb.lo,'#60a5fa',true);}
    if(inds.vol){const maxV=Math.max(...data.v);data.v.forEach((vol,i)=>{const x=xOf(i),bh=(vol/maxV)*cH*0.22,isUp2=data.c[i]>=data.o[i];ctx.fillStyle=(isUp2?(panel.upCol||'#22c55e'):(panel.dnCol||'#ef4444'))+'44';ctx.fillRect(x-bW/2,pad.top+cH-bh,bW,bh);});}
    const lastP=data.c[n-1],lastY=yS(lastP),isUpLast=lastP>=(data.c[0]||lastP);
    ctx.strokeStyle=isUpLast?'#22c55e':'#ef4444';ctx.lineWidth=0.5;ctx.setLineDash([3,2]);ctx.beginPath();ctx.moveTo(pad.left,lastY);ctx.lineTo(pad.left+cW,lastY);ctx.stroke();ctx.setLineDash([]);
    ctx.fillStyle=isUpLast?'#22c55e':'#ef4444';ctx.fillRect(W-pad.right+1,lastY-7,pad.right-2,14);ctx.fillStyle='#000';ctx.textAlign='center';ctx.font='bold 9px monospace';ctx.fillText('$'+fN(lastP),W-pad.right/2,lastY+3);
  },[panel]);
  return <canvas ref={ref} style={{display:'block',width:'100%',height:'100%'}}/>;
}

// ── CHART PANEL ──
function ChartPanel({panel,onRemove,onChange,onPopout,dragHandlers}){
  const [showInd,setShowInd]=useState(false);
  const [showType,setShowType]=useState(false);
  const [showSet,setShowSet]=useState(false);
  const [symInput,setSymInput]=useState(panel.sym);
  const [loading,setLoading]=useState(false);
  const [lastUpdated,setLastUpdated]=useState(null);

  const TFS=['1m','5m','15m','30m','1H','4H','D'];
  const CHART_TYPES=[{val:'candlestick',label:'Candlestick'},{val:'heikinashi',label:'Heikin Ashi'},{val:'line',label:'Line'},{val:'area',label:'Area'},{val:'ohlc',label:'OHLC Bars'}];
  const IND_LIST=[{key:'vwap',label:'VWAP',color:'#a78bfa'},{key:'ema9',label:'EMA 9',color:'#fbbf24'},{key:'ema20',label:'EMA 20',color:'#34d399'},{key:'ema200',label:'EMA 200',color:'#f472b6'},{key:'bb',label:'Bollinger',color:'#60a5fa'},{key:'vol',label:'Volume',color:'#2563eb'}];
  const UP_COLS=['#22c55e','#16a34a','#00e676','#2563eb','#10b981'];
  const DN_COLS=['#ef4444','#dc2626','#f97316','#7c3aed','#e11d48'];
  const BG_COLS=['#0d1117','#161b22','#0f0f0f','#0a0e1a','#111827'];
  const inds=panel.inds||{vwap:true,ema9:true,ema20:true};
  const isUp=(panel.chg||0)>=0;
  const fClass=panel.fsz==='low'?'fl-low':panel.fsz==='mid'?'fl-mid':'fl-hi';
  const fLabel=panel.fsz==='low'?'Low Float':panel.fsz==='mid'?'Mid Float':'Large';
  const tc=TETHER_COLORS.find(c=>c.id===panel.tether);

  const loadData=useCallback(async(sym,tf,silent=false)=>{
    if(!silent)setLoading(true);
    const [candles,quote]=await Promise.all([fetchYahooCandles(sym,tf),fetchYahooQuote(sym)]);
    const updates={};
    if(candles&&candles.c.length>0)updates.data=candles;
    if(quote&&quote.c){updates.chg=+(quote.c-quote.pc).toFixed(2);updates.pchg=+((quote.c-quote.pc)/quote.pc*100).toFixed(2);updates.currentPrice=quote.c;updates.dayHigh=quote.h;updates.dayLow=quote.l;updates.dayOpen=quote.o;updates.vol=quote.v>1e6?(quote.v/1e6).toFixed(1)+'M':quote.v>1e3?(quote.v/1e3).toFixed(0)+'K':''+quote.v;}
    onChange(updates);
    setLastUpdated(new Date().toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',second:'2-digit'}));
    if(!silent)setLoading(false);
  },[onChange]);

  useEffect(()=>{loadData(panel.sym,panel.tf||'5m');},[]);// eslint-disable-line
  useEffect(()=>{const i=setInterval(()=>loadData(panel.sym,panel.tf||'5m',true),60000);return()=>clearInterval(i);},[panel.sym,panel.tf,loadData]);
  useEffect(()=>{if(panel.sym!==symInput){setSymInput(panel.sym);loadData(panel.sym,panel.tf||'5m');}},[panel.sym]);// eslint-disable-line

  const displayPrice=panel.currentPrice||panel.data?.c?.[panel.data.c.length-1];

  return(
    <div
      className="chart-panel"
      style={{borderTop:tc?`2px solid ${tc.color}`:'none'}}
      {...dragHandlers}
    >
      <div className="cp-bar">
        {/* DRAG HANDLE */}
        <div className="drag-handle" title="Drag to rearrange">
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="#484f58" strokeWidth="2">
            <circle cx="9" cy="5" r="1" fill="#484f58"/><circle cx="15" cy="5" r="1" fill="#484f58"/>
            <circle cx="9" cy="12" r="1" fill="#484f58"/><circle cx="15" cy="12" r="1" fill="#484f58"/>
            <circle cx="9" cy="19" r="1" fill="#484f58"/><circle cx="15" cy="19" r="1" fill="#484f58"/>
          </svg>
        </div>

        <div className="cp-sym">
          <div className="cp-dot" style={{background:isUp?'#22c55e':'#ef4444'}}></div>
          <input className="cp-sym-input" value={symInput} onChange={e=>setSymInput(e.target.value.toUpperCase())} onKeyDown={e=>{if(e.key==='Enter'){onChange({sym:symInput});loadData(symInput,panel.tf||'5m');}}} maxLength={6}/>
          {loading&&<div className="cp-loading">⟳</div>}
        </div>

        <div className="cp-tf">
          <select className="cp-select" value={panel.tf||'5m'} onChange={e=>{onChange({tf:e.target.value});loadData(panel.sym,e.target.value);}}>
            {TFS.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div className="cp-dd" style={{position:'relative'}}>
          <button className="cp-icon-btn" onClick={()=>{setShowInd(!showInd);setShowType(false);setShowSet(false);}}>
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 12h4M10 6h11M10 18h11M3 6l2 2 2-2M3 18l2 2 2-2"/></svg><span>Inds</span>
          </button>
          {showInd&&<div className="drop-menu"><div className="drop-header">Indicators</div>{IND_LIST.map(ind=><div key={ind.key} className="drop-row"><div className="drop-left"><div className="drop-dot" style={{background:ind.color}}></div><span>{ind.label}</span></div><button className={`drop-toggle ${inds[ind.key]?'on':'off'}`} onClick={()=>onChange({inds:{...inds,[ind.key]:!inds[ind.key]}})}/></div>)}</div>}
        </div>

        <div className="cp-dd" style={{position:'relative'}}>
          <button className="cp-icon-btn" onClick={()=>{setShowType(!showType);setShowInd(false);setShowSet(false);}}>
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="8" width="4" height="8"/><rect x="10" y="5" width="4" height="14"/><rect x="17" y="10" width="4" height="6"/></svg><span>{CHART_TYPES.find(t=>t.val===(panel.chartType||'candlestick'))?.label||'Candle'}</span>
          </button>
          {showType&&<div className="drop-menu"><div className="drop-header">Chart Type</div>{CHART_TYPES.map(t=><div key={t.val} className="drop-row" onClick={()=>{onChange({chartType:t.val});setShowType(false);}}><span style={{color:(panel.chartType||'candlestick')===t.val?'#60a5fa':'#c9d1d9',fontWeight:(panel.chartType||'candlestick')===t.val?700:400}}>{t.label}</span>{(panel.chartType||'candlestick')===t.val&&<span style={{color:'#60a5fa'}}>✓</span>}</div>)}</div>}
        </div>

        <div className="cp-dd" style={{position:'relative'}}>
          <button className="cp-icon-btn" onClick={()=>{setShowSet(!showSet);setShowInd(false);setShowType(false);}}>
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12"/></svg><span>Settings</span>
          </button>
          {showSet&&<div className="drop-menu" style={{minWidth:'220px',right:0,left:'auto'}}><div className="drop-header">Chart Settings</div><div className="drop-section-label">Up Candle</div><div className="drop-colors">{UP_COLS.map(c=><div key={c} className={`color-swatch ${panel.upCol===c?'sel':''}`} style={{background:c}} onClick={()=>onChange({upCol:c})}/>)}</div><div className="drop-section-label">Down Candle</div><div className="drop-colors">{DN_COLS.map(c=><div key={c} className={`color-swatch ${panel.dnCol===c?'sel':''}`} style={{background:c}} onClick={()=>onChange({dnCol:c})}/>)}</div><div className="drop-section-label">Background</div><div className="drop-colors">{BG_COLS.map(c=><div key={c} className={`color-swatch ${panel.bgCol===c?'sel':''}`} style={{background:c,border:'1px solid #30363d'}} onClick={()=>onChange({bgCol:c})}/>)}</div></div>}
        </div>

        {/* TETHER */}
        <TetherBtn tetherId={panel.tether} onChangeTether={color=>onChange({tether:color})}/>

        {/* REFRESH */}
        <button className="cp-icon-btn" style={{marginLeft:2,color:'#6b7280'}} onClick={()=>loadData(panel.sym,panel.tf||'5m')} title="Refresh data">
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
        </button>

        {/* POPOUT */}
        <PopoutBtn onPopout={onPopout}/>

        <button className="cp-close" onClick={onRemove}>✕</button>
      </div>

      <div className="cp-ohlc">
        <span className="ohlc-sym">{panel.sym}</span>
        <span style={{background:isUp?'#22c55e':'#ef4444',width:7,height:7,borderRadius:'50%',display:'inline-block'}}></span>
        <span className="ohlc-item">O <b>{fN(panel.dayOpen||panel.data?.o?.[0])}</b></span>
        <span className="ohlc-item">H <b style={{color:'#22c55e'}}>{fN(panel.dayHigh)}</b></span>
        <span className="ohlc-item">L <b style={{color:'#ef4444'}}>{fN(panel.dayLow)}</b></span>
        <span className="ohlc-item">C <b style={{color:isUp?'#22c55e':'#ef4444'}}>{fN(displayPrice)}</b>
          <b style={{color:isUp?'#22c55e':'#ef4444'}}> {isUp?'+':''}{fN(panel.chg||0)} ({isUp?'+':''}{fN(panel.pchg||0)}%)</b>
        </span>
        <span className="ohlc-item">Vol <b style={{color:'#60a5fa'}}>{panel.vol||'--'}</b></span>
        <span className={`float-badge ${fClass}`} style={{marginLeft:4}}>{panel.float||'--'} · {fLabel}</span>
        {lastUpdated&&<span style={{fontSize:9,color:'#484f58',marginLeft:'auto'}}>↻ {lastUpdated}</span>}
        <SessionBadge/>
      </div>

      <div className="cp-canvas" onClick={()=>{setShowInd(false);setShowType(false);setShowSet(false);}}>
        {loading?<div className="cp-loading-overlay"><div className="cp-spinner"></div><div style={{fontSize:11,color:'#484f58',marginTop:8}}>Loading {panel.sym}...</div></div>:<ChartCanvas panel={panel}/>}
      </div>
    </div>
  );
}

// ── SCANNER PANEL ──
function ScannerPanel({scanId,scanTether,onClose,onChangeTether,onLoadSymbol,onPopout,dragHandlers}){
  const data=SCANNER_DATA[scanId];
  if(!data)return null;
  const rows=data.rows||[];
  const tc=TETHER_COLORS.find(c=>c.id===scanTether);
  function fszClass(f){return f==='low'?'fl-low':f==='mid'?'fl-mid':'fl-hi';}

  return(
    <div className="chart-panel scanner-panel" style={{borderTop:tc?`2px solid ${tc.color}`:'none'}} {...dragHandlers}>
      <div className="cp-bar" style={{background:'#0d1a2d',borderBottom:'1px solid #1e3a5f'}}>
        {/* DRAG HANDLE */}
        <div className="drag-handle" title="Drag to rearrange">
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="#484f58" strokeWidth="2">
            <circle cx="9" cy="5" r="1" fill="#484f58"/><circle cx="15" cy="5" r="1" fill="#484f58"/>
            <circle cx="9" cy="12" r="1" fill="#484f58"/><circle cx="15" cy="12" r="1" fill="#484f58"/>
            <circle cx="9" cy="19" r="1" fill="#484f58"/><circle cx="15" cy="19" r="1" fill="#484f58"/>
          </svg>
        </div>

        <div className="cp-sym" style={{gap:5,flex:1,minWidth:0}}>
          <span style={{fontSize:13,flexShrink:0}}>🔍</span>
          <span style={{fontSize:11,fontWeight:700,color:'#60a5fa',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{data.title}</span>
          <span style={{fontSize:9,color:'#484f58',flexShrink:0}}>({rows.length})</span>
        </div>

        {/* TETHER */}
        <TetherBtn tetherId={scanTether} onChangeTether={onChangeTether}/>

        {/* POPOUT */}
        <PopoutBtn onPopout={onPopout}/>

        <button className="cp-close" onClick={onClose} style={{marginLeft:2}}>✕</button>
      </div>

      <div style={{padding:'3px 8px',background:'#0a0d12',borderBottom:'1px solid #21262d',fontSize:9,color:'#484f58',flexShrink:0}}>{data.desc}</div>

      <div style={{flex:1,overflowY:'auto'}}>
        <table className="scanner-tbl">
          <thead>
            <tr>
              <th style={{textAlign:'left'}}>Symbol</th>
              <th>Price</th><th>Chg%</th><th>Volume</th><th>Float</th><th>Gap</th><th>RS</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row,i)=>(
              <tr key={i} className="scanner-tbl-row" onClick={()=>onLoadSymbol(row.sym)}>
                <td>
                  <div style={{display:'flex',alignItems:'center',gap:5}}>
                    <div style={{width:24,height:24,borderRadius:6,background:symCol(row.sym),display:'flex',alignItems:'center',justifyContent:'center',fontSize:8,fontWeight:700,color:'#fff',flexShrink:0}}>{row.sym.slice(0,3)}</div>
                    <div>
                      <div style={{fontSize:11,fontWeight:700,color:'#e6edf3'}}>{row.sym}{row.rs>85?' 🔥':''}</div>
                      <span className={`float-badge-sm ${fszClass(row.fsz)}`}>{row.float}</span>
                    </div>
                  </div>
                </td>
                <td style={{fontWeight:700,color:'#e6edf3'}}>${fN(row.price)}</td>
                <td><span style={{color:row.chg>=0?'#22c55e':'#ef4444',fontWeight:700}}>{row.chg>=0?'+':''}{fN(row.chg)}%</span></td>
                <td style={{color:'#60a5fa'}}>{row.vol}</td>
                <td style={{color:'#8b949e'}}>{row.float}</td>
                <td style={{color:row.gap?.startsWith('+')?'#22c55e':'#8b949e'}}>{row.gap}</td>
                <td>
                  <div style={{display:'flex',alignItems:'center',gap:4,justifyContent:'flex-end'}}>
                    <div style={{width:30,height:4,background:'#21262d',borderRadius:2,overflow:'hidden'}}>
                      <div style={{width:row.rs+'%',height:'100%',background:row.rs>80?'#ef4444':row.rs>60?'#f59e0b':'#22c55e',borderRadius:2}}/>
                    </div>
                    <span style={{fontSize:10,color:row.rs>80?'#ef4444':row.rs>60?'#f59e0b':'#22c55e',fontWeight:700,minWidth:20,textAlign:'right'}}>{row.rs}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── MAIN CHARTS PAGE ──
export default function Charts({activeScanId,onScanClose}){
  const [panels,setPanels]=useState([
    {id:1,sym:'AAPL',chartType:'candlestick',tf:'5m',upCol:'#22c55e',dnCol:'#ef4444',bgCol:'#0d1117',inds:{vwap:true,ema9:true,ema20:true},chg:0,pchg:0,float:'15.4B',fsz:'hi',tether:null,data:{t:[],o:[],h:[],l:[],c:[],v:[]}},
    {id:2,sym:'TSLA',chartType:'candlestick',tf:'1m', upCol:'#22c55e',dnCol:'#ef4444',bgCol:'#0d1117',inds:{vwap:true,ema9:true,ema20:true},chg:0,pchg:0,float:'3.17B',fsz:'hi',tether:null,data:{t:[],o:[],h:[],l:[],c:[],v:[]}},
    {id:3,sym:'NVDA',chartType:'candlestick',tf:'5m',upCol:'#22c55e',dnCol:'#ef4444',bgCol:'#0d1117',inds:{vwap:true,ema9:true,ema20:true},chg:0,pchg:0,float:'2.46B',fsz:'hi',tether:null,data:{t:[],o:[],h:[],l:[],c:[],v:[]}},
    {id:4,sym:'SPY', chartType:'candlestick',tf:'D',  upCol:'#22c55e',dnCol:'#ef4444',bgCol:'#0d1117',inds:{vwap:true,ema9:true,ema20:true},chg:0,pchg:0,float:'--',  fsz:'hi',tether:null,data:{t:[],o:[],h:[],l:[],c:[],v:[]}},
  ]);
  const [scanTether,setScanTether]=useState(null);
  const [activeBroker,setActiveBroker]=useState(null);
  const [atSym,setAtSym]=useState('AAPL');
  const [addSym,setAddSym]=useState('');
  const [layout,setLayout]=useState(2);
  const [dragOver,setDragOver]=useState(null);
  const dragSrc=useRef(null);
  const nextId=useRef(10);

  // ── TETHER LOAD ──
  function loadSymbolByTether(sym,tetherId){
    if(!tetherId){setPanels(prev=>prev.map((p,i)=>i===0?{...p,sym}:p));return;}
    setPanels(prev=>prev.map(p=>p.tether===tetherId?{...p,sym}:p));
  }

  // ── DRAG & DROP to rearrange ──
  function handleDragStart(e,id){
    dragSrc.current=id;
    e.dataTransfer.effectAllowed='move';
    e.currentTarget.style.opacity='0.4';
  }
  function handleDragEnd(e){e.currentTarget.style.opacity='1';setDragOver(null);}
  function handleDragOver(e,id){e.preventDefault();setDragOver(id);}
  function handleDrop(e,targetId){
    e.preventDefault();
    const srcId=dragSrc.current;
    if(srcId===targetId)return;
    setPanels(prev=>{
      const arr=[...prev];
      const si=arr.findIndex(p=>p.id===srcId);
      const ti=arr.findIndex(p=>p.id===targetId);
      if(si<0||ti<0)return prev;
      [arr[si],arr[ti]]=[arr[ti],arr[si]];
      return arr;
    });
    setDragOver(null);
  }

  // ── POPOUT to new window ──
  function popoutPanel(item){
    const w=window.open('','_blank','width=900,height=600,resizable=yes,scrollbars=yes');
    if(!w)return;
    if(item.type==='scanner'){
      const data=SCANNER_DATA[item.scanId];
      if(!data)return;
      const rows=data.rows||[];
      w.document.write(`<!DOCTYPE html><html><head>
        <title>${data.title} — The Groove Trading</title>
        <style>
          *{box-sizing:border-box;margin:0;padding:0;}
          body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#0d1117;color:#c9d1d9;}
          .header{background:#161b22;border-bottom:1px solid #21262d;padding:10px 14px;display:flex;align-items:center;gap:8px;}
          .title{font-size:14px;font-weight:700;color:#60a5fa;}
          .desc{font-size:11px;color:#484f58;margin-left:auto;}
          table{width:100%;border-collapse:collapse;}
          th{font-size:9px;color:#484f58;text-transform:uppercase;padding:6px 12px;border-bottom:1px solid #21262d;text-align:right;background:#161b22;position:sticky;top:0;}
          th:first-child{text-align:left;}
          td{padding:8px 12px;border-bottom:1px solid #21262d20;text-align:right;font-size:12px;}
          td:first-child{text-align:left;}
          tr:hover td{background:#21262d30;cursor:pointer;}
          .sym{font-weight:700;color:#e6edf3;font-size:13px;}
          .up{color:#22c55e;font-weight:700;}.dn{color:#ef4444;font-weight:700;}
          .float-lo{background:#166534;color:#86efac;font-size:9px;padding:1px 5px;border-radius:3px;}
          .float-mid{background:#854d0e;color:#fde68a;font-size:9px;padding:1px 5px;border-radius:3px;}
          .float-hi{background:#1e3a5f;color:#93c5fd;font-size:9px;padding:1px 5px;border-radius:3px;}
        </style>
      </head><body>
        <div class="header"><span style="font-size:16px">🔍</span><span class="title">${data.title}</span><span class="desc">${data.desc} · ${rows.length} results</span></div>
        <table><thead><tr><th style="text-align:left">Symbol</th><th>Price</th><th>Chg%</th><th>Volume</th><th>Float</th><th>Gap</th><th>RS</th></tr></thead>
        <tbody>
          ${rows.map(r=>`<tr>
            <td><div class="sym">${r.sym}${r.rs>85?' 🔥':''}</div><span class="${r.fsz==='low'?'float-lo':r.fsz==='mid'?'float-mid':'float-hi'}">${r.float}</span></td>
            <td style="font-weight:700;color:#e6edf3">$${r.price}</td>
            <td class="${r.chg>=0?'up':'dn'}">${r.chg>=0?'+':''}${r.chg}%</td>
            <td style="color:#60a5fa">${r.vol}</td>
            <td style="color:#8b949e">${r.float}</td>
            <td style="color:${r.gap?.startsWith('+')?'#22c55e':'#8b949e'}">${r.gap}</td>
            <td style="color:${r.rs>80?'#ef4444':r.rs>60?'#f59e0b':'#22c55e'};font-weight:700">${r.rs}</td>
          </tr>`).join('')}
        </tbody></table>
      </body></html>`);
      w.document.close();
    } else {
      // Chart popout
      const p=item.panel;
      w.document.write(`<!DOCTYPE html><html><head>
        <title>${p.sym} — The Groove Trading</title>
        <style>
          *{box-sizing:border-box;margin:0;padding:0;}
          body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#0d1117;color:#c9d1d9;height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;}
          .icon{font-size:48px;}
          .title{font-size:18px;font-weight:700;color:#e6edf3;}
          .sub{font-size:12px;color:#484f58;}
          .sym{font-size:32px;font-weight:800;color:#60a5fa;}
          .btn{padding:10px 20px;background:#2563eb;color:#fff;border:none;border-radius:8px;font-size:13px;font-weight:700;cursor:pointer;font-family:inherit;}
        </style>
      </head><body>
        <div class="icon">📈</div>
        <div class="sym">${p.sym}</div>
        <div class="title">${p.tf} Chart — ${p.chartType}</div>
        <div class="sub">Chart rendering in popout windows coming with the Schwab API integration</div>
        <div class="sub" style="color:#22c55e">✅ Symbol: ${p.sym} · Timeframe: ${p.tf}</div>
      </body></html>`);
      w.document.close();
    }
  }

  function addChart(sym){
    const s=(sym||addSym).trim().toUpperCase()||'GME';
    const id=nextId.current++;
    setPanels(prev=>[...prev,{id,sym:s,chartType:'candlestick',tf:'5m',upCol:'#22c55e',dnCol:'#ef4444',bgCol:'#0d1117',inds:{vwap:true,ema9:true,ema20:true},chg:0,pchg:0,float:'--',fsz:'mid',tether:null,data:{t:[],o:[],h:[],l:[],c:[],v:[]}}]);
    if(!sym)setAddSym('');
  }

  function updatePanel(id,changes){setPanels(prev=>prev.map(p=>p.id===id?{...p,...changes}:p));}
  function removePanel(id){setPanels(prev=>prev.filter(p=>p.id!==id));}

  const allItems=[
    ...panels.map(p=>({type:'chart',panel:p,id:p.id})),
    ...(activeScanId?[{type:'scanner',scanId:activeScanId,id:'scan-'+activeScanId}]:[]),
  ];

  return(
    <>
    <div className="charts-page">
      <div className="charts-topbar">
        <div className="ct-search">
          <input className="ct-input" placeholder="Symbol..." value={addSym} onChange={e=>setAddSym(e.target.value.toUpperCase())} onKeyDown={e=>{if(e.key==='Enter')addChart();}} maxLength={6}/>
          <button className="ct-btn prim" onClick={()=>addChart()}>+ Chart</button>
        </div>
        <div className="ct-layouts">
          {[1,2,3].map(n=>(
            <button key={n} className={`ct-btn ${layout===n?'active':''}`} onClick={()=>setLayout(n)}>{n===1?'1×2':n===2?'2×2':'3×2'}</button>
          ))}
        </div>
        <div className="ct-broker-btns">
          <button className="ct-broker-btn tos-btn-launch" onClick={()=>{setActiveBroker(activeBroker==='tos'?null:'tos');setAtSym(panels[0]?.sym||'AAPL');}}>
            <span className="ct-broker-dot tos-dot"/>thinkorswim
          </button>
          <button className="ct-broker-btn wb-btn-launch" onClick={()=>{setActiveBroker(activeBroker==='webull'?null:'webull');setAtSym(panels[0]?.sym||'AAPL');}}>
            <span className="ct-broker-dot wb-dot"/>Webull
          </button>
          <button className="ct-broker-btn ib-btn-launch" onClick={()=>{setActiveBroker(activeBroker==='ibkr'?null:'ibkr');setAtSym(panels[0]?.sym||'AAPL');}}>
            <span className="ct-broker-dot ib-dot"/>IBKR
          </button>
        </div>
        <div className="ct-right">
          <div className="ct-pdt-pill">📋 PDT Rule Changing — Practice Now</div>
          <div style={{fontSize:10,color:'#22c55e'}}>✅ Yahoo Finance Live Data</div>
        </div>
      </div>

      <div style={{flex:1,overflow:'hidden',display:'grid',gridTemplateColumns:`repeat(${layout},1fr)`,gridTemplateRows:`repeat(${Math.ceil(allItems.length/layout)},1fr)`,gap:'1px',background:'#21262d',minHeight:0}}>
        {allItems.map(item=>{
          const isDragOver=dragOver===item.id;
          const dragHandlers={
            draggable:true,
            onDragStart:e=>handleDragStart(e,item.id),
            onDragEnd:handleDragEnd,
            onDragOver:e=>handleDragOver(e,item.id),
            onDrop:e=>handleDrop(e,item.id),
            style:{outline:isDragOver?'2px solid #2563eb':'none',outlineOffset:'-2px'},
          };

          return item.type==='chart'?(
            <ChartPanel
              key={item.panel.id}
              panel={item.panel}
              onRemove={()=>removePanel(item.panel.id)}
              onChange={changes=>updatePanel(item.panel.id,changes)}
              onPopout={()=>popoutPanel(item)}
              dragHandlers={dragHandlers}
            />
          ):(
            <ScannerPanel
              key={item.id}
              scanId={item.scanId}
              scanTether={scanTether}
              onClose={onScanClose}
              onChangeTether={setScanTether}
              onLoadSymbol={sym=>loadSymbolByTether(sym,scanTether)}
              onPopout={()=>popoutPanel(item)}
              dragHandlers={dragHandlers}
            />
          );
        })}
      </div>
    </div>
    {activeBroker&&<ActiveTraderPanel broker={activeBroker} sym={atSym} onClose={()=>setActiveBroker(null)} onTrade={({sym})=>setAtSym(sym)}/>}
    </>
  );
}